import { getBranding } from './branding';
import { isRuntimeTenantResolved } from './runtime';
import { normalizeTenantId, type ResolvedRuntimeConfig, type TenantId } from './runtime/types';

type RuntimeMetadata = {
  title: string;
  description: string;
  canonicalUrl: string;
  faviconHref: string;
  manifestHref: string;
  socialImage: string;
  tenantId: string;
  deploymentTenantId: TenantId | null;
  deploymentConflict: boolean;
};

// Empty/transparent SVG data URI used as the neutral favicon on tenant hostnames
// before their branding resolves. Declaring a favicon (instead of removing icon
// links) stops the browser from auto-requesting /favicon.ico, which would serve
// the deployment's base Trade-Proof favicon on every tenant subdomain.
const NEUTRAL_FAVICON_HREF =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'/%3E";

const NEUTRAL_METADATA: RuntimeMetadata = {
  title: 'Trading workspace',
  description: 'Secure trading workspace.',
  canonicalUrl: '',
  faviconHref: NEUTRAL_FAVICON_HREF,
  manifestHref: '',
  socialImage: '',
  tenantId: 'unresolved',
  deploymentTenantId: null,
  deploymentConflict: false,
};

type RuntimeMetadataOptions = {
  deploymentTenantId?: string | null;
};

const readDeploymentTenantId = () => {
  try {
    return String(import.meta.env.VITE_BRAND || '').trim();
  } catch {
    return '';
  }
};

export function resolveRuntimeMetadata(
  runtime: ResolvedRuntimeConfig,
  options: RuntimeMetadataOptions = {},
): RuntimeMetadata {
  const deploymentTenantValue = options.deploymentTenantId === undefined
    ? readDeploymentTenantId()
    : options.deploymentTenantId;
  const deploymentTenantId = normalizeTenantId(deploymentTenantValue);

  if (!isRuntimeTenantResolved(runtime)) {
    return {
      ...NEUTRAL_METADATA,
      deploymentTenantId,
    };
  }

  if (deploymentTenantId && deploymentTenantId !== runtime.tenant.id) {
    return {
      ...NEUTRAL_METADATA,
      deploymentTenantId,
      deploymentConflict: true,
    };
  }

  const brand = getBranding(runtime.brand.id);
  return {
    title: String(brand.appName || brand.companyName || 'Trading workspace').trim() || 'Trading workspace',
    description: runtime.brand.id === 'warrior-trading'
      ? 'Warrior Trading client workspace.'
      : 'Trading journal, replay, and analytics Performance Intelligence for Serious Traders',
    canonicalUrl: String(brand.websiteUrl || '').trim(),
    faviconHref: deploymentTenantId
      ? '/favicon.ico'
      : String(brand.logos.faviconSvg || brand.logos.favicon || '').trim(),
    manifestHref: deploymentTenantId
      ? '/site.webmanifest'
      : String(brand.logos.manifest || '').trim(),
    socialImage: String(brand.logos.ogImage || '').trim(),
    tenantId: runtime.tenant.id,
    deploymentTenantId,
    deploymentConflict: false,
  };
}

export function applyRuntimeMetadata(
  runtime: ResolvedRuntimeConfig,
  targetDocument: Document = document,
  options: RuntimeMetadataOptions = {},
) {
  const metadata = resolveRuntimeMetadata(runtime, options);

  if (metadata.deploymentConflict) {
    console.warn(
      `[tenant-metadata] Deployment tenant "${metadata.deploymentTenantId}" conflicts with `
      + `runtime hostname tenant "${runtime.tenant.id}". Applying neutral metadata.`,
    );
  }

  applyMetadataToDocument(metadata, targetDocument);
}

/**
 * Neutral boot metadata for tenant hostnames (tenant wildcards and custom domains)
 * whose tenant branding has not resolved yet. Never reveals the base brand: the
 * tenant host always starts neutral so the public branding payload can set the
 * tenant's own title/favicon without ever flashing the default Trade-Proof assets.
 */
export function applyNeutralRuntimeMetadata(targetDocument: Document = document) {
  applyMetadataToDocument(NEUTRAL_METADATA, targetDocument);
}

function applyMetadataToDocument(metadata: RuntimeMetadata, targetDocument: Document) {
  targetDocument.title = metadata.title;
  targetDocument.documentElement.setAttribute('data-bootstrap-tenant', metadata.tenantId);

  const upsertMeta = (
    selector: string,
    attribute: 'name' | 'property',
    key: string,
    content: string,
  ) => {
    let meta = targetDocument.querySelector(selector) as HTMLMetaElement | null;
    if (!meta) {
      meta = targetDocument.createElement('meta');
      meta.setAttribute(attribute, key);
      targetDocument.head.appendChild(meta);
    }
    meta.content = content;
  };

  upsertMeta('meta[name="description"]', 'name', 'description', metadata.description);
  upsertMeta('meta[property="og:title"]', 'property', 'og:title', metadata.title);
  upsertMeta('meta[property="og:description"]', 'property', 'og:description', metadata.description);
  upsertMeta('meta[property="og:url"]', 'property', 'og:url', metadata.canonicalUrl);
  upsertMeta('meta[property="og:image"]', 'property', 'og:image', metadata.socialImage);
  upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', metadata.title);
  upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', metadata.description);
  upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', metadata.socialImage);

  const syncLink = (rel: string, href: string, type?: string) => {
    let link = targetDocument.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
    if (!href) {
      link?.remove();
      return;
    }
    if (!link) {
      link = targetDocument.createElement('link');
      link.rel = rel;
      targetDocument.head.appendChild(link);
    }
    link.href = href;
    if (type) link.type = type;
  };

  if (!metadata.faviconHref) {
    targetDocument
      .querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"], link[rel="manifest"]')
      .forEach((link) => link.remove());
  } else {
    const lowerFavicon = metadata.faviconHref.toLowerCase();
    const isNeutralFavicon = lowerFavicon.startsWith('data:image/svg');
    // Declare exactly one favicon: drop any inherited/duplicate icon links first so
    // a stale base or previous-tenant favicon never survives on a tenant host. The
    // neutral placeholder also drops the apple-touch-icon; real brand deployments
    // keep theirs.
    targetDocument
      .querySelectorAll(
        isNeutralFavicon
          ? 'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]'
          : 'link[rel="icon"], link[rel="shortcut icon"]',
      )
      .forEach((link) => link.remove());
    const faviconType = (isNeutralFavicon || lowerFavicon.endsWith('.svg'))
      ? 'image/svg+xml'
      : 'image/x-icon';
    syncLink('icon', metadata.faviconHref, faviconType);
    syncLink('manifest', metadata.manifestHref);
  }
}
